import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { BookOpen, Loader2, KeyRound, MailQuestion } from "lucide-react";
import { toast } from "sonner";

const SIDE = "https://images.unsplash.com/photo-1643716991951-285e23e35961?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [stage, setStage] = useState("request"); // request | confirm
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/password-reset/request", { email });
      setNotice(data.message);
      setStage("confirm");
    } catch (e2) { setError(apiError(e2)); }
    finally { setLoading(false); }
  };

  const confirmReset = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await api.post("/auth/password-reset/confirm", { email, code, new_password: newPassword });
      toast.success("Password reset. Please sign in with your new password.");
      navigate("/login");
    } catch (e2) { setError(apiError(e2)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAF7EE]">
      <div className="hidden lg:block relative">
        <img src={SIDE} alt="An open Bible beside a candle" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#3D5844]/35" />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="font-heading text-4xl text-[#FAF7EE] leading-snug">Grace to begin again.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <BookOpen className="w-6 h-6 text-[#C46B4E]" strokeWidth={1.6} />
            <span className="font-heading text-2xl">The Peace Plot</span>
          </Link>
          <h1 className="font-heading text-4xl text-[#2A2621]">Reset your password</h1>

          {stage === "request" && (
            <>
              <p className="text-[#615A50] mt-1">Enter your email and your community leader will provide a one-time reset code.</p>
              <form onSubmit={requestCode} className="mt-8 space-y-4" data-testid="reset-request-form">
                <div>
                  <label className="text-sm font-medium text-[#615A50]">Email</label>
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="reset-request-email"
                    className="mt-1.5 w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-3 text-[#2A2621] outline-none focus:ring-2 focus:ring-[#3D5844]/40"
                    placeholder="you@example.com" />
                </div>
                {error && <p className="text-sm text-[#AC5B40]" data-testid="reset-error">{error}</p>}
                <button type="submit" disabled={loading} data-testid="reset-request-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#3D5844] text-white py-3 font-medium hover:bg-[#2F4535] transition-colors disabled:opacity-60">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailQuestion className="w-4 h-4" />}
                  Request reset code
                </button>
              </form>
              <p className="mt-6 text-sm text-[#615A50] text-center">
                Already have a code?{" "}
                <button onClick={() => setStage("confirm")} data-testid="reset-have-code" className="text-[#C46B4E] font-medium hover:underline">Enter it here</button>
              </p>
            </>
          )}

          {stage === "confirm" && (
            <>
              <p className="text-[#615A50] mt-1">Enter the code your leader gave you along with a new password.</p>
              {notice && <div className="mt-4 rounded-xl border border-[#3D5844]/25 bg-[#3D5844]/5 p-4 text-sm text-[#3D5844]" data-testid="reset-notice">{notice}</div>}
              <form onSubmit={confirmReset} className="mt-6 space-y-4" data-testid="reset-confirm-form">
                <div>
                  <label className="text-sm font-medium text-[#615A50]">Email</label>
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="reset-confirm-email"
                    className="mt-1.5 w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-3 text-[#2A2621] outline-none focus:ring-2 focus:ring-[#3D5844]/40"
                    placeholder="you@example.com" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#615A50]">Reset code</label>
                  <input type="text" required value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="reset-confirm-code"
                    className="mt-1.5 w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-3 text-[#2A2621] tracking-[0.3em] font-mono uppercase outline-none focus:ring-2 focus:ring-[#3D5844]/40"
                    placeholder="ABCD1234" maxLength={8} />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#615A50]">New password</label>
                  <input type="password" required value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="reset-confirm-password"
                    className="mt-1.5 w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-3 text-[#2A2621] outline-none focus:ring-2 focus:ring-[#3D5844]/40"
                    placeholder="At least 6 characters" minLength={6} />
                </div>
                {error && <p className="text-sm text-[#AC5B40]" data-testid="reset-error">{error}</p>}
                <button type="submit" disabled={loading} data-testid="reset-confirm-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#3D5844] text-white py-3 font-medium hover:bg-[#2F4535] transition-colors disabled:opacity-60">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                  Set new password
                </button>
              </form>
            </>
          )}

          <p className="mt-6 text-sm text-[#615A50] text-center">
            <Link to="/login" data-testid="reset-to-login" className="text-[#C46B4E] font-medium hover:underline">Back to sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
